import { MCP_TOKENS_QUERY_KEY, useRevokeMcpToken } from '@/lib/mcp-tokens-queries';
import type { McpToken, McpTokenList } from '@mss_creator/shared';
import { ActionBar, Alert, Button, Dialog, DialogClose } from '@pms-digital/design-system';
import { useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

interface McpTokenRevokeDialogProps {
  token: McpToken;
  onClose: () => void;
}

/**
 * Confirmation before revoking a token: the agent that holds it loses access
 * at once and nothing brings it back. Focus lands on « Annuler », so a stray
 * Enter revokes nothing. A failure keeps the dialog open, says the token is
 * still active, and the button becomes « Réessayer ».
 */
export function McpTokenRevokeDialog({ token, onClose }: McpTokenRevokeDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();
  const mutation = useRevokeMcpToken();

  function revoke() {
    mutation.mutate(token.id, {
      onSuccess: onClose,
      // The list was refetched: a token revoked from another tab already reads
      // « Révoqué », so there is nothing left to retry.
      onError: () => {
        const list = queryClient.getQueryData<McpTokenList>(MCP_TOKENS_QUERY_KEY);
        if (list?.tokens.some((entry) => entry.id === token.id && entry.status === 'revoked')) {
          onClose();
        }
      },
    });
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !mutation.isPending) onClose();
      }}
      tone="destructive"
      title={`Révoquer « ${token.name} » ?`}
      description="Un agent qui utilise ce jeton perd l'accès tout de suite. La révocation ne s'annule pas : il faudra créer un nouveau jeton."
      initialFocus={cancelRef}
      actions={
        <ActionBar
          align="center"
          secondary={
            <DialogClose asChild>
              <Button ref={cancelRef} variant="ghost" disabled={mutation.isPending}>
                Annuler
              </Button>
            </DialogClose>
          }
          primary={
            <Button
              variant="destructive"
              loading={mutation.isPending}
              loadingLabel="Révocation…"
              onClick={revoke}
            >
              {mutation.isError ? 'Réessayer' : 'Révoquer le jeton'}
            </Button>
          }
        />
      }
    >
      {mutation.isError ? (
        <div className="text-left">
          <Alert tone="danger" title="La révocation a échoué.">
            Le jeton est toujours actif. Réessayez dans un instant.
          </Alert>
        </div>
      ) : undefined}
    </Dialog>
  );
}
